import { BaseExporter } from "../BaseExporter";
/**
 * EdifExporter - Pure business logic for EDIF netlist generation.
 * This class contains the core data transformation logic for generating EDIF netlists.
 * Browser-specific download functionality should be handled by frontend adapters.
 */
export class EdifExporter extends BaseExporter {
    /**
     * Generates the EDIF content string.
     * This is the pure business logic that can be used in both frontend and backend.
     */
    generateEdifContent() {
        const documentName = this.documentData.name || "FluxDocument";
        const timestamp = new Date().toISOString();
        const edifContent = this.buildEdifSExpression(documentName, timestamp);
        return edifContent;
    }
    /**
     * Creates a filename for the EDIF export.
     */
    createEdifFileName() {
        return this.createFileName("edif");
    }
    buildEdifSExpression(documentName, timestamp) {
        const libraries = this.generateLibraries();
        const design = this.generateDesign(documentName);
        return `(edif ${this.escapeEdifString(documentName)}
    (edifVersion 2 0 0)
    (edifLevel 0)
    (keywordMap (keywordLevel 0))
    (status
        (written
            (timestamp ${timestamp.replace(/[-:]/g, " ").replace("T", " ").replace("Z", "")})
            (program "Flux" (version "1.0"))
            (author "Flux")
        )
    )
    ${libraries}
    ${design}
)`;
    }
    generateLibraries() {
        const libraryParts = new Map();
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(this.documentData.elements).forEach((element) => {
            const partUid = element.part_uid;
            const partName = element.part_version_data_cache?.name || partUid;
            if (!libraryParts.has("FluxLibrary")) {
                libraryParts.set("FluxLibrary", new Set());
            }
            libraryParts.get("FluxLibrary").add(partName);
        });
        let librariesContent = "";
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        libraryParts.forEach((parts, libraryName) => {
            const cellsContent = Array.from(parts)
                .map((partName) => this.generateCell(partName))
                .join("\n        ");
            librariesContent += `(library ${this.escapeEdifString(libraryName)}
        (edifLevel 0)
        (technology (numberDefinition))
        ${cellsContent}
    )
    `;
        });
        return librariesContent;
    }
    generateCell(partName) {
        const element = Object.values(this.documentData.elements).find((el) => (el.part_version_data_cache?.name || el.part_uid) === partName);
        const terminals = element?.part_version_data_cache?.terminals || {};
        const interfaceContent = Object.values(terminals)
            .map((terminal) => `(port ${this.escapeEdifString(terminal.name || terminal.uid)} (direction inout))`)
            .join("\n                ");
        return `(cell ${this.escapeEdifString(partName)}
            (cellType generic)
            (view schematic
                (viewType schematic)
                (interface
                    ${interfaceContent}
                )
            )
        )`;
    }
    generateDesign(documentName) {
        const instances = this.generateInstances();
        const nets = this.generateNets();
        return `(library Main
            (edifLevel 0)
            (technology (numberDefinition))
            (cell ${this.escapeEdifString(documentName + "_top")}
                (cellType generic)
                (view schematic
                    (viewType schematic)
                    (interface)
                    (contents
                        ${instances}
                        ${nets}
                    )
                )
            )
        )
        (design ${this.escapeEdifString(documentName)}
            (cellRef ${this.escapeEdifString(documentName + "_top")} (libraryRef Main))
        )`;
    }
    generateInstances() {
        return Object.values(this.documentData.elements)
            .map((element) => {
            const partName = element.part_version_data_cache?.name || element.part_uid;
            const instanceName = element.label || element.uid;
            return `(instance ${this.escapeEdifString(instanceName)}
                (viewRef schematic (cellRef ${this.escapeEdifString(partName)} (libraryRef FluxLibrary)))
            )`;
        })
            .join("\n                        ");
    }
    generateNets() {
        return Object.values(this.documentData.nets)
            .map((net) => {
            const netName = net.label || net.uid;
            const connections = this.getNetConnections(net.uid);
            const joinedConnections = connections
                .map((conn) => `(portRef ${this.escapeEdifString(conn.terminalName)} (instanceRef ${this.escapeEdifString(conn.instanceName)}))`)
                .join("\n                            ");
            return `(Net ${this.escapeEdifString(netName)}
                            (joined ${joinedConnections})
                        )`;
        })
            .join("\n                        ");
    }
    getNetConnections(netUid) {
        const connections = [];
        const net = this.documentData.nets[netUid];
        if (net && net.pins) {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            Object.values(net.pins).forEach((pin) => {
                const element = this.documentData.elements[pin.elementUid];
                if (element) {
                    const terminal = element.part_version_data_cache?.terminals?.[pin.terminalUid];
                    if (terminal) {
                        connections.push({
                            instanceName: element.label || element.uid,
                            terminalName: terminal.name || pin.terminalUid,
                        });
                    }
                }
            });
        }
        return connections;
    }
    escapeEdifString(str) {
        if (/[^a-zA-Z0-9_]/.test(str)) {
            return `"${str.replace(/"/g, '\\"')}"`;
        }
        return str;
    }
}
//# sourceMappingURL=EdifExporter.js.map